import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff, LogIn, Compass, BarChart3, MessageCircle, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Remplis tous les champs");
      return;
    }
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.must_change_password) {
        navigate("/change-password");
      } else if (data.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
      toast.success(`Bienvenue ${data.student_name || "Admin"} !`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* LEFT SIDE - Hero */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Background image */}
        <img
          src="https://images.unsplash.com/photo-1705414302203-fca10a50ef6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxvbGQlMjBzYWlsaW5nJTIwc2hpcCUyMGNyZXclMjBzdW5zZXQlMjBnb2xkZW4lMjBhZ2UlMjBleHBsb3JhdGlvbiUyMGdhbGxlb258ZW58MHx8fHwxNzcyMDI1NjEyfDA&ixlib=rb-4.1.0&q=85&w=1920"
          alt="Galion au coucher de soleil"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1c]/90 via-[#0a0f1c]/70 to-[#0a0f1c]/95" />
        {/* Top warm glow */}
        <div className="absolute top-0 right-0 w-[60%] h-[40%] bg-gradient-to-bl from-[#FF6B35]/15 via-[#FF8C42]/10 to-transparent" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Odys'IA" className="h-14 w-14 rounded-xl object-contain shadow-lg" data-testid="login-logo-desktop" />
            <span className="text-white font-bold text-xl tracking-tight">Odys'<span className="text-[#F5B731]">IA</span></span>
          </div>

          {/* Headline */}
          <div className="max-w-lg">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight">
              Ton &eacute;quipage<br />
              <span className="text-[#00D4AA]">immobilier</span><br />
              &agrave; port&eacute;e de main
            </h1>
            <p className="mt-6 text-white/60 text-base leading-relaxed max-w-md">
              Plateforme d'intelligence artificielle premium. Echange avec tes 
              experts IA, analyse tes projets et atteins tes objectifs 
              d'investissement.
            </p>
          </div>

          {/* Feature bullets */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full border border-[#00D4AA]/30 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-[#00D4AA]" />
              </div>
              <span className="text-white/70 text-sm">12 agents IA specialises a ton service</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full border border-[#00D4AA]/30 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-[#00D4AA]" />
              </div>
              <span className="text-white/70 text-sm">Analyse, strategie et recherche web en temps reel</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full border border-[#00D4AA]/30 flex items-center justify-center">
                <Link2 className="w-4 h-4 text-[#00D4AA]" />
              </div>
              <span className="text-white/70 text-sm">Connecte a ta formation OdysInvest</span>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - Login */}
      <div className="w-full lg:w-[45%] bg-[#0a0f1c] flex items-center justify-center p-6 sm:p-10 relative">
        {/* Subtle bg texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />

        <div className="relative z-10 w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center justify-center gap-3 mb-10 lg:hidden">
            <img src="/logo-mark.png" alt="Odys'IA" className="h-12 w-12 rounded-xl object-contain shadow-lg" data-testid="login-logo-mobile" />
            <span className="text-white font-bold text-lg">Odys'<span className="text-[#F5B731]">IA</span></span>
          </div>

          {/* Welcome */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white tracking-tight" data-testid="login-title">
              Bon retour !
            </h2>
            <p className="text-white/40 text-sm mt-2">
              Connecte-toi a ton espace IA
            </p>
          </div>

          {/* Login Card */}
          <div className="bg-[#111827]/60 border border-white/[0.06] rounded-2xl p-7 backdrop-blur-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-white/50 uppercase tracking-wider" htmlFor="email">
                  Email
                </label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="vous@exemple.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 focus-visible:ring-[#00D4AA]/50 focus-visible:border-[#00D4AA]/30 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-white/50 uppercase tracking-wider" htmlFor="password">
                  Mot de passe
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPw ? "text" : "password"}
                    placeholder="Ton mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 rounded-xl bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 focus-visible:ring-[#00D4AA]/50 focus-visible:border-[#00D4AA]/30 pr-10 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-xs text-white/30 hover:text-[#00D4AA] transition-colors"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublie ?
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full h-12 bg-gradient-to-r from-[#00D4AA] to-[#00B894] hover:from-[#00E5BB] hover:to-[#00D4AA] text-[#0a0f1c] font-semibold rounded-xl shadow-lg shadow-[#00D4AA]/15 hover:shadow-[#00D4AA]/25 active:scale-[0.98] transition-all"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[#0a0f1c]/30 border-t-[#0a0f1c] rounded-full animate-spin" />
                ) : (
                  <>Se connecter</>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-[10px] uppercase tracking-widest text-white/30 font-semibold">ou</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Google Sign-In - Emergent-managed OAuth */}
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + "/dashboard";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="google-signin-btn"
              className="w-full h-12 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-white font-semibold flex items-center justify-center gap-3 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.68 9c0-.593.102-1.17.284-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.166 6.656 3.58 9 3.58z"/>
              </svg>
              <span>Se connecter avec Google</span>
            </button>

            {/* Register link */}
            <p className="text-center text-xs text-white/30 mt-6">
              Pas encore de compte ?{" "}
              <a 
                href="https://odysinvest.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[#00D4AA] font-medium hover:underline"
                data-testid="register-link"
              >
                S'inscrire
              </a>
            </p>
          </div>

          {/* CGU */}
          <p className="text-center text-[10px] text-white/15 mt-6">
            En te connectant, tu acceptes nos{" "}
            <button
              type="button"
              onClick={() => navigate("/cgu")}
              className="text-white/25 hover:text-[#00D4AA] underline transition-colors"
            >
              CGU
            </button>
          </p>
        </div>
      </div>

      {/* Fix autofill navigateur : empeche le fond blanc + texte invisible quand
          un identifiant enregistre est selectionne */}
      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-text-fill-color: #fff !important;
          -webkit-box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.04) inset !important;
          box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.04) inset !important;
          caret-color: #fff !important;
          transition: background-color 99999s ease-in-out 0s, color 99999s ease-in-out 0s;
          background-clip: content-box !important;
        }
        input:-webkit-autofill::first-line {
          color: #fff !important;
          font-family: inherit !important;
        }
      `}</style>
    </div>
  );
}
